/**
 * Versions d'une gamme à l'écran : ce que dit chaque confirmation, quelles machines se rattachent
 * en lot, et le bilan d'un rattachement partiel.
 *
 * Fonctions pures, sans React : c'est ici que se décide ce qu'on annonce à l'utilisateur AVANT un
 * geste irréversible (publier est immuable), et c'est donc ici que cela se teste.
 */
import type { GammeDetail, GammeVersion, MachineCorrespondanteGamme } from '../api/client';
import { dateFr } from './gammes';

/** Le jour que voit l'utilisateur, « 2026-10-08 », en heure LOCALE (pas en UTC : après minuit local, le jour a changé). */
export function aujourdhuiLocal(maintenant: Date = new Date()): string {
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${maintenant.getFullYear()}-${deux(maintenant.getMonth() + 1)}-${deux(maintenant.getDate())}`;
}

/** Vrai si cette version est celle qui s'applique aujourd'hui aux machines rattachées. */
export function estEnVigueur(detail: GammeDetail, version: GammeVersion): boolean {
  return version.statut === 'PUBLIEE' && detail.enVigueur?.versionNumber === version.versionNumber;
}

/** Ce qu'une publication change pour les machines rattachées (une seule règle, pour une gamme comme pour N). */
export type EffetDePublication =
  /** La date est atteinte et au moins aussi récente que celle de la version en vigueur : elle prend le relais. */
  | 'MAINTENANT'
  /** La date d'effet est dans le futur : la version en vigueur reste en place d'ici là. */
  | 'PLUS_TARD'
  /** La date est atteinte mais plus ancienne que celle de la version en vigueur : elle ne s'appliquera jamais. */
  | 'SANS_EFFET';

/**
 * L'effet d'une publication : elle ne remplace la version en vigueur que si sa date d'effet est atteinte et au
 * moins aussi récente (à date égale, le numéro départage : la nouvelle l'emporte).
 */
export function effetDePublication(
  validFrom: string,
  enVigueur: { readonly validFrom: string } | null,
  aujourdhui: string,
): EffetDePublication {
  if (validFrom > aujourdhui) return 'PLUS_TARD';
  if (!enVigueur || validFrom >= enVigueur.validFrom) return 'MAINTENANT';
  return 'SANS_EFFET';
}

/**
 * Ce qu'on annonce avant de publier. Publier est IMMUABLE : le contenu, les prix et la date d'effet
 * ne changent plus jamais ; on dit aussi ce que la publication change pour les machines — elle ne
 * remplace la version en vigueur que si sa date d'effet est atteinte et au moins aussi récente.
 */
export function confirmationPublication(
  version: GammeVersion,
  enVigueur: { readonly versionNumber: number; readonly validFrom: string } | null,
  aujourdhui: string,
): string {
  const base =
    `Publier la version ${version.versionNumber} ?\n\n` +
    'Une version publiée est IMMUABLE : son contenu, ses prix et sa date d’effet ne changeront plus ' +
    'jamais. Toute correction ultérieure créera une nouvelle version.';

  const date = dateFr(version.validFrom);
  const effet = effetDePublication(version.validFrom, enVigueur, aujourdhui);
  if (effet === 'PLUS_TARD') {
    return enVigueur
      ? `${base}\n\nAttention : elle ne s’appliquera qu’à partir du ${date}. D’ici là, la version ${enVigueur.versionNumber} reste en vigueur.`
      : `${base}\n\nAttention : elle ne s’appliquera qu’à partir du ${date}. D’ici là, la gamme n’aura aucune version en vigueur.`;
  }
  if (!enVigueur) {
    return `${base}\n\nElle sera la version en vigueur dès maintenant.`;
  }
  if (effet === 'MAINTENANT') {
    return `${base}\n\nElle prendra effet tout de suite : elle remplacera la version ${enVigueur.versionNumber} pour les machines rattachées.`;
  }
  return (
    `${base}\n\nElle ne deviendra PAS la version en vigueur : la version ${enVigueur.versionNumber}, ` +
    'dont la date d’effet est plus récente, reste en vigueur.'
  );
}

/**
 * Ce qui empêche de publier ce brouillon, dit en français, ou `null` s'il est publiable. Un brouillon
 * sans aucun palier ne se publie pas : l'API le refuse, autant le dire AVANT la confirmation
 * « IMMUABLE » plutôt qu'après.
 */
export function obstacleAPublication(version: GammeVersion): string | null {
  if (version.contenu.levels.length === 0) {
    return 'Ce brouillon ne contient aucun palier : composez-le (« Modifier le contenu ») avant de le publier.';
  }
  return null;
}

/** Ce qu'on annonce avant d'archiver une version publiée : rien ne se supprime, mais elle ne s'applique plus. */
export function confirmationArchivage(version: GammeVersion, enVigueur: boolean): string {
  const base =
    `Archiver la version ${version.versionNumber} ?\n\n` +
    'Elle ne s’applique plus aux machines, mais son contenu reste intact et lisible : rien n’est supprimé.';
  return enVigueur
    ? `${base}\n\nC’est la version en vigueur : la précédente version publiée reprendra le relais. ` +
        'S’il n’y en a aucune et que des machines sont rattachées, l’archivage sera refusé.'
    : base;
}

export function confirmationAbandon(version: GammeVersion): string {
  return (
    `Abandonner le brouillon de la version ${version.versionNumber} ?\n\n` +
    'Il ne sera jamais publié. Il est archivé, pas supprimé : rien n’est perdu.'
  );
}

/** Une machine peut être rattachée en lot : pas déjà rattachée, gamme active, avec une version en vigueur. */
export function machineRattachable(detail: GammeDetail, machine: MachineCorrespondanteGamme): boolean {
  return detail.archivedAt === null && detail.enVigueur !== null && machine.rattacheeA === null;
}

/** Les machines cochées d'emblée : rattachables ET sans avertissement de référence — on ne coche jamais un doute. */
export function machinesCocheesParDefaut(detail: GammeDetail): string[] {
  return detail.machinesCorrespondantes
    .filter((m) => machineRattachable(detail, m) && m.avertissementReference === null)
    .map((m) => m.id);
}

/** Phrase courte de l'avertissement d'une machine, pour la liste (la phrase longue va en infobulle). */
export function avertissementCourt(m: MachineCorrespondanteGamme): string | null {
  const a = m.avertissementReference;
  if (!a) return null;
  return a.type === 'ABSENTE'
    ? 'référence non renseignée'
    : `réf. ${a.referenceMachine ?? '?'} ≠ ${a.referencesGamme.join(' ou ')}`;
}

export interface ResultatRattachement {
  readonly machine: string;
  /** `null` : rattachée. Sinon le message de refus de l'API. */
  readonly erreur: string | null;
}

/** Le bilan d'un rattachement en lot : ce qui a réussi, et ce qui a été refusé et pourquoi. */
export function bilanRattachement(resultats: readonly ResultatRattachement[]): {
  readonly succes: string | null;
  readonly refus: string | null;
} {
  const reussies = resultats.filter((r) => r.erreur === null).length;
  const refusees = resultats.filter((r) => r.erreur !== null);
  const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

  return {
    succes: reussies > 0 ? `${pluriel(reussies, 'machine')} rattachée${reussies > 1 ? 's' : ''}.` : null,
    refus:
      refusees.length > 0
        ? `${pluriel(refusees.length, 'machine')} refusée${refusees.length > 1 ? 's' : ''} : ` +
          refusees.map((r) => `${r.machine} (${r.erreur})`).join(' ; ')
        : null,
  };
}
